import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SignInDropdown from './SignInDropdown';
import { isAdminAuthenticated, logoutAdmin } from '../../services/adminAuthService';
import { checkMoverStatus, signOutMover } from '../../services/moverAuthService';
import toast from 'react-hot-toast';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMover, setIsMover] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();

  // Check admin and mover authentication status on mount and location change
  useEffect(() => {
    setIsAdmin(isAdminAuthenticated());
    checkMoverStatus().then(({ isApproved, isTeamMember: isTeam }) => {
      setIsMover(isApproved);
      setIsTeamMember(isTeam || false);
    });
  }, [location]);

  const handleAdminLogout = async () => {
    await logoutAdmin();
    setIsAdmin(false);
    setShowDropdown(false);
    toast.success('Logged out successfully.');
    navigate('/', { replace: true });
  };

  const handleMoverLogout = async () => {
    await signOutMover();
    setIsMover(false);
    setIsTeamMember(false);
    setShowDropdown(false);
    toast.success('Logged out successfully.');
    navigate('/', { replace: true });
  };

  // Also listen for storage changes (in case of logout in another tab)
  useEffect(() => {
    const handleStorageChange = () => {
      setIsAdmin(isAdminAuthenticated());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showDropdown]);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img src="/moverslogo.png" alt="MyMoveAdvisor" className="h-24" />
          </Link>

          <div className="hidden md:flex items-center space-x-6 relative">
            {/* Navigation for GUESTS (not logged in) */}
            {!isAuthenticated && !isAdmin && !isMover && (
              <>
                <Link
                  to="/quote"
                  className="group bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-xl font-bold transition-all duration-300 flex items-center shadow-lg hover:shadow-primary/30 transform hover:-translate-y-0.5"
                >
                  Get Free Quote
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </>
            )}

            {/* Navigation for LOGGED-IN USERS (Customer) */}
            {isAuthenticated && user?.role === 'user' && (
              <>
                <Link to="/" className="text-gray-700 hover:text-primary">Home</Link>
                <Link to="/user" className="text-gray-700 hover:text-primary font-medium">User Dashboard</Link>
              </>
            )}

            {/* Navigation for ADMIN */}
            {isAdmin && (
              <>
                <Link to="/" className="text-gray-700 hover:text-primary">Home</Link>
                <Link to="/admin/dashboard" className="text-gray-700 hover:text-primary font-medium">Admin Dashboard</Link>
              </>
            )}

            {/* Navigation for MOVER */}
            {isMover && !isAuthenticated && !isAdmin && (
              <>
                <Link to="/" className="text-gray-700 hover:text-primary">Home</Link>
                <Link to="/dashboard" className="text-gray-700 hover:text-primary font-medium">Mover Dashboard</Link>
              </>
            )}

            {/* Sign In Button for GUESTS */}
            {!isAuthenticated && !isAdmin && !isMover && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown((v) => !v)}
                  className="group relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 border-2 border-gray-100 text-gray-600 hover:text-primary hover:border-primary/30 hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
                  aria-label="Account Menu"
                >
                  <svg className="w-5 h-5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-primary border-2 border-white rounded-full"></span>
                </button>
                {showDropdown && (
                  <SignInDropdown onClose={() => setShowDropdown(false)} />
                )}
              </div>
            )}

            {/* Logout Buttons */}
            {isAuthenticated && user?.role === 'user' && (
              <button
                onClick={() => { logout(); toast.success('Logged out successfully.'); setShowDropdown(false); navigate('/'); }}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition"
              >
                Sign Out
              </button>
            )}

            {isMover && !isAuthenticated && !isAdmin && (
              <button
                onClick={handleMoverLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition"
              >
                Logout
              </button>
            )}

            {isAdmin && (
              <button
                onClick={handleAdminLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition"
              >
                Logout (Admin)
              </button>
            )}
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden pb-4">
            {/* Navigation for GUESTS (not logged in) */}
            {!isAuthenticated && !isAdmin && !isMover && (
              <>
                <Link
                  to="/quote"
                  className="bg-primary text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center shadow-lg mb-2"
                  onClick={() => setIsOpen(false)}
                >
                  Get Free Quote
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </>
            )}

            {/* Navigation for LOGGED-IN USERS (Customer) */}
            {isAuthenticated && user?.role === 'user' && (
              <>
                <Link to="/" className="block py-2 text-gray-700">Home</Link>
                <Link to="/user" className="block py-2 text-gray-700 font-medium">User Dashboard</Link>
              </>
            )}

            {/* Navigation for ADMIN */}
            {isAdmin && (
              <>
                <Link to="/" className="block py-2 text-gray-700">Home</Link>
                <Link to="/admin/dashboard" className="block py-2 text-gray-700 font-medium">Admin Dashboard</Link>
              </>
            )}

            {/* Navigation for MOVER */}
            {isMover && !isAuthenticated && !isAdmin && (
              <>
                <Link to="/" className="block py-2 text-gray-700">Home</Link>
                <Link to="/dashboard" className="block py-2 text-gray-700 font-medium">Mover Dashboard</Link>
              </>
            )}

            {/* Sign In Button for GUESTS */}
            {!isAuthenticated && !isAdmin && !isMover && (
              <div className="mt-2 border-t pt-2">
                <button
                  onClick={() => setShowDropdown((v) => !v)}
                  className="flex items-center gap-3 p-2 rounded-xl bg-gray-50 border border-gray-100 text-gray-700 hover:text-primary hover:bg-white hover:shadow-sm transition-all duration-300 w-full"
                  aria-label="Sign In"
                >
                  <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                    <svg className="w-5 h-5 text-gray-500 group-hover:text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span className="font-semibold">Sign In / Account</span>
                </button>
                {showDropdown && (
                  <div className="relative" ref={dropdownRef}>
                    <SignInDropdown onClose={() => setShowDropdown(false)} />
                  </div>
                )}
              </div>
            )}

            {/* Logout Buttons */}
            {isAuthenticated && user?.role === 'user' && (
              <button
                onClick={() => { logout(); toast.success('Logged out successfully.'); setShowDropdown(false); navigate('/'); }}
                className="mt-2 bg-red-500 hover:bg-red-600 text-white w-full px-4 py-2 rounded-lg transition"
              >
                Sign Out
              </button>
            )}

            {isMover && !isAuthenticated && !isAdmin && (
              <button
                onClick={handleMoverLogout}
                className="mt-2 bg-red-500 hover:bg-red-600 text-white w-full px-4 py-2 rounded-lg transition"
              >
                Logout
              </button>
            )}

            {isAdmin && (
              <button
                onClick={handleAdminLogout}
                className="mt-2 bg-red-500 hover:bg-red-600 text-white w-full px-4 py-2 rounded-lg transition"
              >
                Logout (Admin)
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
